import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Package, Plus, Edit, Trash2, Loader2, Search, ToggleLeft, ToggleRight,
  MapPin, Truck, AlertTriangle, Phone, Building2, X,
} from 'lucide-react';
import { authHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import SupplierForm, { formatDocumentoDisplay } from '../components/SupplierForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_LABELS = {
  Local: { label: 'Local de Operação', color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  Externo: { label: 'Fornecedor Externo', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  'Nao Classificado': { label: 'Não Classificado', color: 'text-gray-400 bg-gray-500/10 border-gray-500/30' },
};

export default function Fornecedores({ userRole }) {
  const { user } = useAuth();
  const canEdit = user?.is_master || userRole === 'admin';
  const [loading, setLoading] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterAtivo, setFilterAtivo] = useState('');
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);
  const [filterUF, setFilterUF] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterTipoPessoa, setFilterTipoPessoa] = useState('');
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterUF) params.append('uf', filterUF);
      if (filterCidade) params.append('cidade', filterCidade);
      if (filterTipoPessoa) params.append('tipo_pessoa', filterTipoPessoa);
      const r = await fetch(`${API_URL}/api/fornecedores?${params}`, { headers: authHeaders() });
      if (r.ok) setFornecedores(await r.json());
    } catch { toast.error('Erro ao carregar fornecedores'); }
    finally { setLoading(false); }
  }, [filterUF, filterCidade, filterTipoPessoa]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Carrega UFs distintas
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/fornecedores/uf-distintas`, { headers: authHeaders() });
        if (r.ok) setUfs(await r.json());
      } catch { /* noop */ }
    })();
  }, [fornecedores.length]);

  // Carrega cidades quando UF muda
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filterUF) params.append('uf', filterUF);
        const r = await fetch(`${API_URL}/api/fornecedores/cidades-distintas?${params}`, { headers: authHeaders() });
        if (r.ok) setCidades(await r.json());
      } catch { /* noop */ }
    })();
  }, [filterUF, fornecedores.length]);

  const filtered = fornecedores.filter(f => {
    if (search && !f.nome.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTipo && f.tipo !== filterTipo) return false;
    if (filterAtivo === 'ativo' && !f.ativo) return false;
    if (filterAtivo === 'inativo' && f.ativo) return false;
    if (showOnlyIncomplete && f.dados_completos) return false;
    return true;
  });

  const handleToggle = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/fornecedores/${id}/toggle-status`, {
        method: 'PATCH', headers: authHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(data.message);
        fetchData();
      } else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Excluir fornecedor "${nome}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/fornecedores/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.ok) { toast.success('Fornecedor excluído'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const stats = {
    total: fornecedores.length,
    ativos: fornecedores.filter(f => f.ativo).length,
    incompletos: fornecedores.filter(f => !f.dados_completos).length,
    externos: fornecedores.filter(f => f.tipo === 'Externo').length,
  };

  if (loading && !fornecedores.length) return (
    <div className="flex items-center justify-center h-64" data-testid="fornecedores-loading">
      <Loader2 className="animate-spin text-yellow-500" size={40} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="fornecedores-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Fornecedores</h1>
          <p className="text-sm text-gray-400 mt-1">CNPJ, endereço, contato e vínculos com pedidos</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 text-sm"
                  data-testid="add-fornecedor-btn">
            <Plus size={16} /> Novo Fornecedor
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats.total} icon={Package} />
        <StatCard label="Ativos" value={stats.ativos} icon={ToggleRight} color="text-emerald-400" />
        <StatCard label="Dados Incompletos" value={stats.incompletos} icon={AlertTriangle} color="text-orange-400" />
        <StatCard label="Externos" value={stats.externos} icon={Truck} color="text-blue-400" />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar fornecedor por nome..." data-testid="fornecedor-search"
                 className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </div>
        <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="filter-tipo">
          <option value="">Todos os Tipos</option>
          <option value="Local">Local de Operação</option>
          <option value="Externo">Fornecedor Externo</option>
          <option value="Nao Classificado">Não Classificado</option>
        </select>
        <select value={filterAtivo} onChange={(e) => setFilterAtivo(e.target.value)}
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" data-testid="filter-ativo">
          <option value="">Todos</option>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm cursor-pointer">
          <input type="checkbox" checked={showOnlyIncomplete}
                 onChange={(e) => setShowOnlyIncomplete(e.target.checked)}
                 data-testid="filter-incompletos"
                 className="accent-orange-400" />
          Só com dados incompletos
        </label>
        <select value={filterTipoPessoa} onChange={(e) => setFilterTipoPessoa(e.target.value)}
                data-testid="filter-tipo-pessoa"
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm">
          <option value="">PF e PJ</option>
          <option value="PJ">Apenas PJ</option>
          <option value="PF">Apenas PF</option>
        </select>
        <select value={filterUF}
                onChange={(e) => { setFilterUF(e.target.value); setFilterCidade(''); }}
                data-testid="filter-uf"
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm">
          <option value="">Todas UFs</option>
          {ufs.map(uf => <option key={uf} value={uf}>{uf}</option>)}
        </select>
        <select value={filterCidade} onChange={(e) => setFilterCidade(e.target.value)}
                data-testid="filter-cidade"
                className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm">
          <option value="">Todas cidades</option>
          {cidades.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="fornecedor-modal">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5">
            <SupplierForm
              fornecedor={editing}
              onClose={() => { setShowForm(false); setEditing(null); }}
              onSaved={() => { setShowForm(false); setEditing(null); fetchData(); }}
            />
          </div>
        </div>
      )}

      <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="fornecedores-table">
            <thead>
              <tr className="border-b border-gray-800 bg-[#0A0A0A]">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">CPF/CNPJ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Cidade/UF</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Telefone / Contato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                {canEdit && <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={canEdit ? 7 : 6} className="px-4 py-8 text-center text-gray-500">Nenhum fornecedor encontrado</td></tr>
              ) : filtered.map(f => (
                <tr key={f.id} className={`border-b border-gray-800/50 hover:bg-gray-900/50 ${!f.ativo ? 'opacity-50' : ''}`}
                    data-testid={`fornecedor-row-${f.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {f.tipo === 'Local' ? <MapPin size={14} className="text-blue-400 shrink-0" /> : <Truck size={14} className="text-orange-400 shrink-0" />}
                      <div>
                        <p className="text-sm text-white">{f.nome}</p>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {f.tipo_pessoa === 'PF' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-300 border border-purple-500/30 rounded">PF</span>
                          )}
                          {!f.dados_completos && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-orange-500/10 text-orange-300 border border-orange-500/30 rounded">
                              <AlertTriangle size={9} /> dados incompletos
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                    {f.documento ? formatDocumentoDisplay(f.documento, f.tipo_pessoa) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {f.cidade || f.uf ? (
                      <span>{f.cidade || '—'}{f.uf ? <span className="text-gray-500"> / {f.uf}</span> : ''}</span>
                    ) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {f.telefone || f.pessoa_contato ? (
                      <div className="flex flex-col">
                        {f.telefone && <span className="flex items-center gap-1"><Phone size={11} className="text-gray-500" /> {f.telefone}</span>}
                        {f.pessoa_contato && <span className="flex items-center gap-1 text-gray-400 text-xs"><Building2 size={11} /> {f.pessoa_contato}</span>}
                      </div>
                    ) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full border ${(TIPO_LABELS[f.tipo] || TIPO_LABELS.Externo).color}`}>
                      {(TIPO_LABELS[f.tipo] || TIPO_LABELS.Externo).label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${f.ativo ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'}`}>
                      {f.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleToggle(f.id)} title={f.ativo ? 'Inativar' : 'Ativar'}
                                className="p-1.5 text-gray-400 hover:text-yellow-500" data-testid={`toggle-fornecedor-${f.id}`}>
                          {f.ativo ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} />}
                        </button>
                        <button onClick={() => { setEditing(f); setShowForm(true); }}
                                className="p-1.5 text-gray-400 hover:text-yellow-500" data-testid={`edit-fornecedor-${f.id}`}>
                          <Edit size={14} />
                        </button>
                        {!f.location_id && (
                          <button onClick={() => handleDelete(f.id, f.nome)}
                                  className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-fornecedor-${f.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'text-yellow-500' }) {
  return (
    <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-gray-900 ${color}`}><Icon size={18} /></div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
}
